<script>
    import { onMount } from "svelte";

    import Select from "svelte-select/Select.svelte";

    import {
        Button,
        CardLayout,
        NotificationModal,
        PageLoading,
    } from "../../Components/Common/index.svelte";

    import { user } from "../../stores";

    import { DirectUserRoles } from "../../Utils/utilEnums.svelte";

    import {
        goBack,
        isObjectEmpty,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { cacheAllGroups } from "../../Utils/utilCache.svelte";

    import { searchGroupsFromCache } from "../../Utils/DataLoaders.svelte";

    import {
        defaultCustomisationConfig,
        defaultGroup,
    } from "./PortalCustomisationConstants.svelte";

    import PortalCustomSection from "./PortalCustomSection.svelte";
    import PortalPreviewSection from "./PortalPreviewSection.svelte";

    $: isUserAdmin = $user.role == DirectUserRoles.Admin;
    $: isUserManager = $user.role == DirectUserRoles.Manager;

    $: isLoading = isInitiatingConfig || isLoadingConfig;

    let customConfig = { ...defaultCustomisationConfig },
        fontFamilyConfig = {},
        topHeaderConfig = {},
        formConfig = {},
        isCreateProcess = true,
        isCustomisingPortal = false;

    let selectedGroup = { group: defaultGroup };

    let isInitiatingConfig = false,
        isLoadingConfig = false;

    let showResetConfirmModal = false;

    let editorRef;

    onMount(async () => {
        if (!isUserAdmin && !isUserManager) goBack();

        isInitiatingConfig = true;

        if (isUserManager) await loadCustomisation($user.groupId);

        if (isUserAdmin) {
            await cacheAllGroups();
            await handleSelectGroup();
        }

        isInitiatingConfig = false;
    });

    const loadCustomisation = async (groupId) => {
        isLoadingConfig = true;

        await apiMethods.getMethod
            .getPortalCustomisationConfigByGroupId(groupId)
            .then((res) => {
                if (!res || res.error) {
                    showError(
                        res.error ??
                            "Failed to load portal customisation config, please refresh the page and try again and contact support if the issue persists.",
                    );
                    goBack();
                } else {
                    isCreateProcess = res.data === "";

                    _resetConfig();

                    if (!isCreateProcess)
                        try {
                            customConfig = JSON.parse(res.data);
                            fontFamilyConfig = {
                                ...defaultCustomisationConfig,
                                ...customConfig,
                            }.fontFamily;

                            topHeaderConfig = {
                                ...defaultCustomisationConfig,
                                ...customConfig,
                            }.topHeader;

                            formConfig = {
                                ...defaultCustomisationConfig,
                                ...customConfig,
                            }.form;
                        } catch (e) {
                            _resetConfig();
                        }
                }
            });

        isLoadingConfig = false;
    };

    const handleResetPortal = async () => {
        isCustomisingPortal = true;

        const config = {};

        if (isUserManager) config.groupId = $user.groupId;
        else config.groupId = selectedGroup?.group?.id ?? Number(0);

        config.data = JSON.stringify({
            ...defaultCustomisationConfig,
        }).toString();

        await apiMethods.putMethod
            .savePortalCustomisation(config)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to reset portal customisation details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else _resetConfig();
            });

        _closeResetConfirmModal();

        isCustomisingPortal = false;
    };

    const handleCustomisePortal = async () => {
        isCustomisingPortal = true;

        const config = {};

        if (isUserManager) config.groupId = $user.groupId;
        else config.groupId = selectedGroup?.group?.id ?? Number(0);

        config.data = JSON.stringify({
            ...customConfig,
            topHeader: topHeaderConfig,
            form: formConfig,
            fontFamily: fontFamilyConfig,
        });
        // config.data = "";

        await apiMethods.putMethod
            .savePortalCustomisation(config)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to save portal customisation details, please refresh the page and try again and contact support if the issue persists.",
                    );
            });

        isCustomisingPortal = false;
    };

    const handleSelectGroup = async () => {
        if (
            !selectedGroup ||
            isObjectEmpty(selectedGroup) ||
            !selectedGroup.group
        )
            return;

        const { id: groupId } = selectedGroup.group;

        await loadCustomisation(groupId);
    };

    // HELPER FUNCTIONS
    const _resetConfig = () => {
        customConfig = { ...defaultCustomisationConfig };
        topHeaderConfig = customConfig.topHeader;
        formConfig = customConfig.form;
        fontFamilyConfig = customConfig.fontFamily;
        if (editorRef) editorRef.setContent(topHeaderConfig.content);
    };

    const _openResetConfirmModal = () => (showResetConfirmModal = true);

    const _closeResetConfirmModal = () => (showResetConfirmModal = false);
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex items-start justify-end gap-x-4">
        {#if isUserAdmin}
            <div class="flex flex-1 items-center gap-x-2">
                <p>Select group:</p>

                <Select
                    bind:value={selectedGroup.group}
                    class="flex flex-1 rounded-full"
                    loadOptions={async (text) => {
                        const searchRes = await searchGroupsFromCache(text);
                        return [defaultGroup, ...searchRes];
                    }}
                    itemId="id"
                    label="name"
                    on:select={handleSelectGroup}
                    noOptionsMessage={"No groups found"}
                    disabled={isLoading}
                    clearable={false}
                />
            </div>
        {/if}

        <div class="flex flex-1 gap-x-2 justify-end">
            <Button clickFunc={_openResetConfirmModal}>Reset</Button>

            <Button
                clickFunc={handleCustomisePortal}
                isDisabled={isCustomisingPortal}
            >
                {isCustomisingPortal ? "Saving" : "Save"}
            </Button>
        </div>
    </div>

    <div class="flex flex-1 relative">
        <CardLayout title="Portal Customisations">
            {#if isLoading}
                <PageLoading />
            {:else}
                <div
                    class="flex gap-x-2 flex-1 absolute inset-0 py-8 pt-16 px-4"
                >
                    <PortalCustomSection
                        bind:formConfig
                        bind:topHeaderConfig
                        bind:fontFamilyConfig
                        bind:editorRef
                    />

                    <div class="divider divider-horizontal" />

                    <div
                        class="flex flex-col flex-1 gap-y-2 p-2 overflow-y-auto"
                    >
                        <PortalPreviewSection
                            {fontFamilyConfig}
                            {topHeaderConfig}
                            {formConfig}
                        />
                    </div>
                </div>
            {/if}
        </CardLayout>
    </div>
</div>

<NotificationModal
    isModalOpen={showResetConfirmModal}
    title="Reset Customisations"
    isReverseBtns
    cancelText="Reset"
    closeModal={handleResetPortal}
    submitText="Not now"
    confirmAction={_closeResetConfirmModal}
    closeModalFunction={_closeResetConfirmModal}
>
    <p class="leading-6 p-4">
        Are you sure you want to reset all customisations?
    </p>
</NotificationModal>

<style>
    .w-20 {
        width: 20%;
    }
</style>
